export default (ngModule) => {
  ngModule.run(
    (
      $rootScope,
      $state,
      $stateParams,
      Auth,
      $log,
      $location,
      AonApplicationService,
      AonConfig
    ) => {
      /**
       * Get a field of the state object. Inherits values from parent states - child states take precedence.
       * @param stateName
       * @param fieldName
       * @returns {Object}
       */
      function getStateFieldInherited(stateName, fieldName) {
        let data = {};
        let parts = stateName.split(".");
        while (parts.length) {
          const stateData = $state.get(parts.join("."));
          if (stateData && stateData[fieldName]) {
            data = angular.extend(angular.copy(stateData[fieldName]), data);
          }
          parts.pop();
        }
        return data;
      }

      // References to state variables so templates can easily use them, eg. add class based on active route
      $rootScope.$state = $state;
      $rootScope.$stateParams = $stateParams;
      $rootScope.$on(
        "$stateChangeError",
        function (event, toState, toParams, fromState, fromParams, error) {
          if (_.isObject(error) && error.code === 401) {
            $state.go("welcome");
          } else {
            $log.debug(arguments);
          }
        }
      );

      // Check for an operator query string and override AonConfig entry with it if present
      const checkOperator = () => {
        if ($location.search().hasOwnProperty("operator")) {
          AonConfig.operator.operator_type = $location.search().operator;
        }
      };

      // Handling authorisation on routes
      // NOTE: This won't stop route transitions. If this becomes problematic then move to a solution that always
      // calls event.preventDefault(), checks auth and then if valid re-fires $state.go without firing event
      $rootScope.$on("$stateChangeStart", function (event, toState) {
        checkOperator();
        const applyFromApplicationId = !!$location.search().applicationId;
        const require = applyFromApplicationId
          ? {}
          : getStateFieldInherited(toState.name, "require");
        const baseRoute = AonConfig.corporate.is_operator
          ? "operator"
          : "comparator";

        if (require.auth) {
          Auth.check()["catch"](function () {
            if ($location.search().hasOwnProperty("code")) {
              const success = () => {};
              const error = () => {
                $state.go("welcome");
              };
              Auth.login($location.search().code).then(success, error);
            } else {
              $state.go("welcome");
            }
          });
        }

        if (require.price && !AonApplicationService.hasPrice()) {
          if (toState.name === `${baseRoute}.step2`) {
            console.error(
              "Route loop detected; step 2 should not require price!"
            );
          } else {
            event.preventDefault();
            $state.go(`${baseRoute}.step2`);
          }
        }

        if (
          require.applicant &&
          !AonApplicationService.hasAddressAndState() &&
          !/[?&]quote=/.test(location.href)
        ) {
          if (toState.name === `${baseRoute}.step1`) {
            console.error(
              "Route loop detected; step 1 should not require state!"
            );
          } else {
            event.preventDefault();
            $state.go(`${baseRoute}.step1`);
          }
        }

        if (require.passwordRetrieval) {
          if (!AonConfig.corporate.allow_code_email_retrieval) {
            event.preventDefault();
            $state.go("welcome");
          }
        }
      });
    }
  );

  ngModule.config(($stateProvider, $urlRouterProvider, AonConfig) => {
    $urlRouterProvider.otherwise("/welcome");

    // Allow a per-corporate override on the application page template url
    const templateUrlCorporateOverride = (defaultName) => () => {
      const templateName =
        AonConfig.corporate.apply_page_template || defaultName;
      return `/static/partials/${templateName}.html?d=201506051053`;
    };

    $stateProvider
      .state("welcome", {
        url: "/welcome",
        template: require("../partials/landing.html"),
        controller: "WelcomeCtrl",
        controllerAs: "ctrl",
      })
      .state("retrievepassword", {
        url: "/retrievepassword",
        template: require("../partials/retrieve-password.html"),
        controller: "RetrievePasswordCtrl",
        controllerAs: "ctrl",
        require: {
          passwordRetrieval: true,
        },
      })
      .state("faq", {
        url: "/faq",
        template: require("../partials/faq.html"),
        controllerAs: "ctrl",
        controller: [
          "AonConfig",
          function (AonConfig) {
            this.deployment = AonConfig.deployment;
          },
        ],
      })
      .state("cookies", {
        url: "/cookies",
        template: require("../partials/cookies.html"),
        controllerAs: "ctrl",
        controller: [
          "AonConfig",
          function (AonConfig) {
            this.deployment = AonConfig.deployment;
          },
        ],
      })
      .state("legal", {
        url: "/legal",
        template: require("../partials/legal.html"),
        controllerAs: "ctrl",
        controller: [
          "AonConfig",
          function (AonConfig) {
            this.deployment = AonConfig.deployment;
            this.corporate = AonConfig.corporate;
          },
        ],
      })
      .state("privacy", {
        url: "/privacy",
        template: require("../partials/privacy.html"),
        controllerAs: "ctrl",
        controller: [
          "AonConfig",
          function (AonConfig) {
            this.deployment = AonConfig.deployment;
          },
        ],
      })
      .state("contact", {
        url: "/contact",
        template: require("../partials/contact.html"),
        controllerAs: "ctrl",
        controller: [
          "AonConfig",
          function (AonConfig) {
            this.content = AonConfig.corporate.contact_html;
          },
        ],
      })
      .state("operator", {
        template: require("../partials/operator-root.html"),
        url: "/operator",
        controller: "RootCtrl",
        controllerAs: "ctrl",
        require: {
          auth: true,
        },
        resolve: {
          // This call is to fetch the initial, pre-selected services, either using
          // data from zoho or the regular service pre-selection. It is not used in
          // this controller, but is a dependency of steps 1 and 2.
          preselectedServices: (AonApplicationService, AonProducts) => {
            return AonApplicationService.getApplication().then((applicant) => {
              return AonProducts.getPreselectedServices(applicant).then(
                (services) => {
                  AonProducts.saveServices(services);
                }
              );
            });
          },
        },
      })
      .state("operator.step1", {
        url: "/about",
        template: require("../partials/operator-step1.html"),
        controller: "OperatorStep1Ctrl",
        controllerAs: "ctrl",
        resolve: {
          application: (AonApplicationService) =>
            AonApplicationService.getApplication(),
          // Note: we need to include preselectedServices as a parameter so that angular
          // knows to wait for the parent state to resolve that promise before fetching
          // the services again. See also step 2 where this is used.
          services: (
            AonApplicationService,
            AonProducts,
            preselectedServices
          ) => {
            return AonApplicationService.getApplication().then((applicant) => {
              return AonProducts.getServices(applicant);
            });
          },
        },
      })
      .state("operator.step2", {
        url: "/compare",
        template: require("../partials/operator-step2.html"),
        resolve: {
          application: (AonApplicationService) =>
            AonApplicationService.getApplication(),
          services: (
            AonApplicationService,
            AonProducts,
            preselectedServices
          ) => {
            return AonApplicationService.getApplication().then((applicant) => {
              return AonProducts.getServices(applicant);
            });
          },
        },
        controller: "OperatorStep2Ctrl",
        controllerAs: "ctrl",
      })

      .state("comparator", {
        template: require("../partials/comparator-root.html"),
        controller: "RootCtrl",
        controllerAs: "ctrl",
        require: {
          auth: true,
        },
      })
      .state("comparator.step1", {
        url: "/about",
        template: require("../partials/comparator-step1.html"),
        controller: "ComparatorStep1Ctrl",
        controllerAs: "ctrl",
        resolve: {
          application: (AonApplicationService) =>
            AonApplicationService.getApplication(),
        },
      })
      .state("comparator.step2", {
        url: "/compare?quote",
        template: require("../partials/comparator-step2.html"),
        require: {
          applicant: true,
        },
        resolve: {
          services: (AonApplicationService, AonProducts) => {
            return AonApplicationService.getApplication().then((applicant) => {
              return AonProducts.getPreselectedServices(applicant);
            });
          },
        },
        controller: "ComparatorStep2Ctrl",
        controllerAs: "ctrl",
      })
      .state("comparator.retention", {
        url: "/apply",
        template: require("../partials/retention.html"),
        require: {
          price: true,
        },
        controller: "ComparatorRetentionCtrl",
        controllerAs: "ctrl",
        resolve: {
          application: (AonApplicationService) =>
            AonApplicationService.getApplication(),
        },
      })
      .state("comparator.step3", {
        url: "/apply?applicationId&priceId",
        template: require("../partials/comparator-step3.html"),
        require: {
          price: true,
        },
        controller: "ComparatorStep3Ctrl",
        controllerAs: "ctrl",
        resolve: {
          loadFromApplication: (
            AonApplicationService,
            AonApi,
            $location,
            Auth,
            $state
          ) => {
            if (__IS_DEV__) {
              // In local dev, allow loading the application page directly by appending
              // a query param: applicationId=1234
              const applicationId = $location.search().applicationId;
              const priceId = $location.search().priceId;
              if (applicationId) {
                Auth.zohoLogin();

                return AonApi.get(
                  `load-application-json/${applicationId}`
                ).then((response) => {
                  const json = response.data;
                  AonApplicationService.getApplication().then((applicant) => {
                    AonApplicationService.saveApplication(json);
                    AonApi.fetchPrice(
                      applicant,
                      priceId || json.product.price.id
                    ).then(({ data: pkg }) => {
                      const price = pkg.price;
                      pkg.selectedPrice = price;
                      AonApplicationService.setProduct(pkg);
                      AonApplicationService.setPrice(price);
                      if (!$location.path().split("/").includes("rebate")) {
                        $state.go(`comparator.step3.${price.fund.code}`, {
                          applicationId,
                          priceId,
                        });
                      }
                    });
                  });
                });
              }
            } else {
              return Promise.resolve();
            }
          },
          // The following resolves need to include loadFromApplication, so that they wait for the
          // above promise to resolve before doing their own things.
          fieldsets: (AonApplicationService, loadFromApplication) =>
            AonApplicationService.getFieldSets(),
          application: (AonApplicationService, loadFromApplication) =>
            AonApplicationService.getApplication(),
          zohoStage: (
            AonApplicationService,
            ZohoService,
            loadFromApplication
          ) =>
            AonApplicationService.getApplication().then((applicant) => {
              if (applicant.zoho_id) {
                return ZohoService.getStage(applicant.zoho_id);
              } else {
                return null;
              }
            }),
        },
      })

      // -- Per-fund application routes --//
      // ACA (non-gateway fund)
      .state("comparator.step3.aca", {
        url: "/aca",
        controller: "ComparatorStep3CtrlHAMBS",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.hambs.html?d=201506051053",
      })
      .state("comparator.step3.ahb", {
        url: "/ahb",
        controller: "ComparatorStep3CtrlHAMBS",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.ahb.html?d=201510200651",
      })
      .state("comparator.step3.ahm", {
        url: "/ahm",
        controller: "ComparatorStep3CtrlAHM",
        controllerAs: "ahmctrl",
        templateUrl: "/static/partials/apply.ahm.html",
      })
      // Australian Unity
      .state("comparator.step3.auf", {
        url: "/auf",
        controller: "ComparatorStep3CtrlAUF",
        controllerAs: "aufctrl",
        templateUrl: "/static/partials/apply.auf.html?d=201506051053",
      })
      // HUNTER HEALTH (non-gateway fund)
      .state("comparator.step3.cdh", {
        url: "/cdh",
        controller: "ComparatorStep3CtrlNoGateway",
        controllerAs: "nogatewayCtrl",
        templateUrl: "/static/partials/apply.nogateway.html?d=201506051053",
      })
      // EMERGENCY SERVICES HEALTH (non-gateway fund)
      .state("comparator.step3.esh", {
        url: "/esh",
        controller: "ComparatorStep3CtrlHAMBS",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.hambs.html?d=201506051053",
      })
      .state("comparator.step3.gmh02", {
        url: "/frank",
        templateUrl: "/static/partials/apply.gmh.html?d=201506051053",
      })
      // GMHBA
      .state("comparator.step3.gmh", {
        url: "/gmh",
        templateUrl: "/static/partials/apply.gmh.html?d=201506051053",
      })
      .state("comparator.step3.hbf", {
        url: "/hbf",
        controller: "ComparatorStep3CtrlHBF",
        controllerAs: "hbfctrl",
        templateUrl: "/static/partials/apply.hbf.html?d=201511241532",
      })
      .state("comparator.step3.hcf", {
        url: "/hcf",
        controller: "ComparatorStep3CtrlHCF",
        controllerAs: "hcfctrl",
        templateUrl: "/static/partials/apply.hcf.html?d=201506051053",
      })
      .state("comparator.step3.hcfcorp", {
        url: "/hcf",
        controller: "ComparatorStep3CtrlHCF",
        controllerAs: "hcfctrl",
        templateUrl: "/static/partials/apply.hcf.html?d=201506051053",
      })
      // HIF
      .state("comparator.step3.hif", {
        url: "/hif",
        controller: "ComparatorStep3CtrlHIF",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.hambs.html?d=201506051053",
      })
      // PEOPLECARE
      .state("comparator.step3.lhm", {
        url: "/lhm",
        controller: "ComparatorStep3CtrlHAMBS",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.hambs.html?d=201506051053",
      })
      // MYOWN
      .state("comparator.step3.myo", {
        url: "/myo",
        controller: "ComparatorStep3CtrlHAMBS",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.hambs.html?d=201506051053",
      })
      // AIA (re-branding of MYOWN)
      .state("comparator.step3.aia", {
        url: "/aia",
        controller: "ComparatorStep3CtrlHAMBS",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.hambs.html?d=201506051053",
      })
      // NAVY HEALTH
      .state("comparator.step3.nhb", {
        url: "/nhb",
        controller: "ComparatorStep3CtrlNHB",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.nhb.html?d=201506051053",
      })
      .state("comparator.step3.nib", {
        url: "/nib",
        controller: "ComparatorStep3CtrlNIB",
        controllerAs: "nibctrl",
        templateUrl: "/static/partials/apply.nib.html?d=201506051053",
      })
      // NURSES + MIDWIVES HEALTH
      .state("comparator.step3.nmw", {
        url: "/nmw",
        templateUrl: "/static/partials/apply.ntf.html?d=201506051053",
      })
      // TEACHERS HEALTH
      .state("comparator.step3.ntf", {
        url: "/ntf",
        templateUrl: "/static/partials/apply.ntf.html?d=201506051053",
      })
      // PHOENIX
      .state("comparator.step3.pwa", {
        url: "/pwa",
        controller: "ComparatorStep3CtrlHAMBS",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.hambs.html?d=201506051053",
      })
      // HCI
      .state("comparator.step3.hci", {
        url: "/hci",
        controller: "ComparatorStep3CtrlHCI",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.hci.html?d=201506051053",
      })
      // TUH
      .state("comparator.step3.qtu", {
        url: "/tuh",
        controller: "ComparatorStep3CtrlHAMBS",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.hambs.html?d=201506051053",
      })
      // Union HEALTH
      .state("comparator.step3.qtu01", {
        url: "/uhf",
        controller: "ComparatorStep3CtrlHAMBS",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.hambs.html?d=201506051053",
      })
      // RT HEALTH
      .state("comparator.step3.rte", {
        url: "/rte",
        controller: "ComparatorStep3CtrlRTE",
        controllerAs: "rtectrl",
        templateUrl: "/static/partials/apply.rte.html?d=201506051053",
      })
      // POLICE HEALTH (non-gateway fund)
      .state("comparator.step3.spe", {
        url: "/spe",
        controller: "ComparatorStep3CtrlNoGateway",
        controllerAs: "nogatewayCtrl",
        templateUrl: "/static/partials/apply.nogateway.html?d=201506051053",
      })
      // HEALTH PARTNERS (based on RT health application)
      .state("comparator.step3.sps", {
        url: "/sps",
        controller: "ComparatorStep3CtrlSPS",
        controllerAs: "subCtrl",
        templateUrl: templateUrlCorporateOverride("apply.sps"),
      })
      // TRANSPORT HEALTH (non-gateway fund)
      .state("comparator.step3.tfs", {
        url: "/tfs",
        controller: "ComparatorStep3CtrlHAMBS",
        controllerAs: "hambsCtrl",
        templateUrl: "/static/partials/apply.hambs.html?d=201506051053",
      })
      // UNIHEALTH
      .state("comparator.step3.uni", {
        url: "/uni",
        templateUrl: "/static/partials/apply.ntf.html?d=201506051053",
      })
      // MEDIBANK
      .state("comparator.step3.mbp", {
        /*
          Medibank does not transition to step 3. On application submission, the
          backend generates a redirect url to Medibank's site, and we redirect there.
          */
        url: "/mbp",
        resolve: {
          GenerateReference: (
            AonApplicationService,
            AonConfig,
            $q,
            $state,
            $log
          ) => {
            AonApplicationService.getApplication().then((app) => {
              const onError = () => {
                AonApplicationService.product.isTransitioningToApply = false;
                $state.go("comparator.error", {
                  message:
                    "This product is currently unavailable through this service. You can use the contact details to advise this error.",
                });
              };
              const onSuccess = (response) => {
                if (AonApplicationService.isDebug()) {
                  AonApplicationService.product.isTransitioningToApply = false;
                  $log.info(response.redirect_to);
                } else {
                  AonApplicationService.trackEvent(
                    AonConfig.jsConstants.TrackingEvents.Application,
                    AonApplicationService.product.fund.id
                  );
                  window.location.href = response.redirect_to;
                }
              };
              AonApplicationService.submitApplication(app).then(
                onSuccess,
                onError
              );
            });
            return $q.reject();
          },
        },
      })
      .state("comparator.step3.rebate", {
        url: "/rebate",
        template: require("../partials/comparator-step3-rebate.html"),
        resolve: {
          application: (AonApplicationService) =>
            AonApplicationService.getApplication(),
        },
      })
      .state("comparator.step4", {
        url: "/success",
        template: require("../partials/comparator-step4.html"),
        controller: "ComparatorStep4Ctrl",
        controllerAs: "ctrl",
        resolve: {
          response: (AonApplicationService) =>
            AonApplicationService.getResponse(),
          application: (AonApplicationService) =>
            AonApplicationService.getApplication(),
        },
      })
      .state("comparator.error", {
        url: "/error",
        template: require("../partials/comparator-error.html"),
        controllerAs: "ctrl",
        controller: function ($stateParams) {
          this.message = $stateParams.message;
        },
        params: {
          message: null,
        },
      })
      .state("comparator.zoho", {
        url: "/zoho/:leadId?price&operator",
        template: require("../partials/zoho-landing.html"),
        controller: "ZohoCtrl",
        controllerAs: "ctrl",
        require: {
          auth: false,
        },
      })
      .state("quote", {
        url: "/quote/:leadId/:priceId?phone",
        template: require("../partials/quote.html"),
        controller: "QuoteCtrl",
        controllerAs: "ctrl",
        require: {
          auth: false,
        },
      })
      .state("price-summary", {
        url: "/price-summary/:priceId",
        template: require("../partials/price-summary.html"),
        controller: "PriceSummaryCtrl",
        controllerAs: "ctrl",
        require: {
          auth: false,
        },
      });
  });
};



// WEBPACK FOOTER //
// ./src/config/routes.js